import {
  downloadBasketsAccountingCsv,
  downloadPaymentsCsv,
  downloadPeriodAccountingKpiXlsx,
  downloadPortVieuxVatStatementXlsx,
  downloadRevenueIntegrationCsv,
  downloadWorksConcilHistoriesCsv,
  useAccountingKpis,
  useAccountingWarnings,
} from '@gozoki/api'
import { FLAGS } from '@gozoki/feature-flags'
import { frenchFormat } from '@gozoki/tools'
import {
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ArrowDownToLine } from 'lucide-react'
import { DateTime } from 'luxon'
import { useCallback, useMemo, useState } from 'react'
import { AuthorizedButton } from '~/components/communication/AuthorizedButton'
import { useDownloadFileMutation } from '~/hooks/use-download-file-mutation'
import { useFeatureFlag } from '~/hooks/useFeatureFlag'
import type { PermissionTuple } from '~/utils/ability'
import { usePermissionGuard, withRoutePermissions } from '~/utils/rbac'
import { moduleIndexPermission } from '~/utils/rbacModuleIndex'
import Page from '../../components/Page'
import { StoresPicker } from '../../utils/dashboard/storesPicker'
import { DashboardCard } from '../../utils/dashboard/dashboardCards'
import { fonts } from '../../utils/theme/fonts.theme'

const READ_FIGURES: PermissionTuple = ['read', 'accounting.accounting.figures']
const EXPORT_FIGURES: PermissionTuple = ['export', 'accounting.accounting.figures']
const EXPORT_BALANCE: PermissionTuple = ['export', 'accounting.accounting.balance']
const EXPORT_PAYMENTS: PermissionTuple = ['export', 'accounting.accounting.payments']
const EXPORT_CE_HISTORY: PermissionTuple = ['export', 'accounting.accounting.works-council-history']
const READ_WARNINGS: PermissionTuple = ['read', 'accounting.accounting.warnings']

/**
 * Le module Comptabilité tient ses cinq ressources sur un seul écran : chaque bloc et chaque export
 * garde la sienne ci-dessous, et l'écran s'ouvre dès qu'une seule est accordée — un rôle « export de
 * balance » ou « alertes comptables » n'avait sinon ni entrée de menu ni écran (BLOQ-04).
 */
const ACCOUNTING_PAGE_READ = moduleIndexPermission([
  READ_FIGURES,
  EXPORT_FIGURES,
  EXPORT_BALANCE,
  EXPORT_PAYMENTS,
  EXPORT_CE_HISTORY,
  READ_WARNINGS,
])

export const Route = createFileRoute('/_authenticated/accounting')({
  staticData: {
    permission: ACCOUNTING_PAGE_READ,
  },
  component: AccountingPage,
})
withRoutePermissions(Route)
const yersterday = DateTime.now().minus({ days: 1 })
const aWeekAgo = DateTime.now().minus({ days: 7 })
const lastMonth = DateTime.now().minus({ months: 1 }).startOf('month')

interface BasketExportProgress {
  completedDays: number
  currentDate: string
  totalDays: number
}

function AccountingPage() {
  const [currentDate, setCurrentDate] = useState<DateTime>(yersterday)

  const [currentStartDate, setCurrentStartDate] = useState<DateTime>(aWeekAgo)
  const [currentEndDate, setCurrentEndDate] = useState<DateTime>(yersterday)
  const [statementMonth, setStatementMonth] = useState<DateTime>(lastMonth)

  const [isLoading, setIsLoading] = useState(false)
  const [basketExportProgress, setBasketExportProgress] = useState<BasketExportProgress | null>(
    null,
  )

  const currentFormatedDate = useMemo(() => currentDate.toFormat('yyyy-MM-dd'), [currentDate])
  const currentFormatedStartDate = useMemo(
    () => currentStartDate.toFormat('yyyy-MM-dd'),
    [currentStartDate],
  )
  const currentFormatedEndDate = useMemo(
    () => currentEndDate.toFormat('yyyy-MM-dd'),
    [currentEndDate],
  )

  // Sélection de boutiques des alertes : `warnings` est une ressource de liaison `selection`, le
  // serveur borne la sélection au périmètre d'affectation (audit du 19/09, M-5).
  const [warningStoreIds, setWarningStoreIds] = useState<number[]>([])

  const canReadFigures = usePermissionGuard(READ_FIGURES).authorized
  const canReadWarnings = usePermissionGuard(READ_WARNINGS).authorized

  const { data: accounting } = useAccountingKpis(currentFormatedDate, canReadFigures)
  const { data: warnings } = useAccountingWarnings(warningStoreIds, canReadWarnings)

  const comptaExportPanierEnabled = useFeatureFlag(FLAGS.comptaExportPanier)

  const { mutate: downloadPaymentsFile } = useDownloadFileMutation({
    mutationFn: async ({ dateStart, dateEnd }: { dateStart: string; dateEnd: string }) => {
      const response = await downloadPaymentsCsv({ dateStart, dateEnd })

      return {
        blob: new Blob([response.data]),
        filename: `paiements-${dateStart}_${dateEnd}.zip`,
      }
    },
    onSettled: () => setIsLoading(false),
  })

  const { mutate: downloadRevenueIntegrationFile } = useDownloadFileMutation({
    mutationFn: async ({ dateStart, dateEnd }: { dateStart: string; dateEnd: string }) => {
      const response = await downloadRevenueIntegrationCsv({ dateStart, dateEnd })
      const format = (isoDate: string) => DateTime.fromISO(isoDate).toFormat('dd_MM_yyyy')

      return {
        blob: new Blob([response.data], { type: 'text/csv;charset=utf-8' }),
        filename: `Intégration Chiffre d'affaires_du_${format(dateStart)}_au_${format(dateEnd)}.csv`,
      }
    },
    onSettled: () => setIsLoading(false),
  })

  const { mutate: downloadBasketsExportFile } = useDownloadFileMutation({
    mutationFn: async ({ dateStart, dateEnd }: { dateStart: string; dateEnd: string }) => {
      const startDate = DateTime.fromISO(dateStart, { zone: 'Europe/Paris' }).startOf('day')
      const endDate = DateTime.fromISO(dateEnd, { zone: 'Europe/Paris' }).startOf('day')
      const totalDays = Math.floor(endDate.diff(startDate, 'days').days) + 1

      if (!startDate.isValid || !endDate.isValid || totalDays < 1) {
        throw new Error('La date de fin doit être postérieure ou égale à la date de début')
      }

      const chunks: Blob[] = []

      for (let dayIndex = 0; dayIndex < totalDays; dayIndex += 1) {
        const day = startDate.plus({ days: dayIndex })
        const isoDate = day.toFormat('yyyy-MM-dd')

        setBasketExportProgress({
          completedDays: dayIndex,
          currentDate: day.toFormat('dd/MM/yyyy'),
          totalDays,
        })

        // Sequential requests keep database load bounded and preserve chronological CSV order.
        // oxlint-disable-next-line no-await-in-loop
        const response = await downloadBasketsAccountingCsv({
          dateStart: isoDate,
          dateEnd: isoDate,
          includeHeader: dayIndex === 0,
        })
        chunks.push(response.data)

        setBasketExportProgress({
          completedDays: dayIndex + 1,
          currentDate: day.toFormat('dd/MM/yyyy'),
          totalDays,
        })
      }

      return {
        blob: new Blob(chunks, { type: 'text/csv;charset=utf-8' }),
        filename: `Export panier_comptabilité_du_${startDate.toFormat('dd_MM_yyyy')}_au_${endDate.toFormat('dd_MM_yyyy')}.csv`,
      }
    },
    onError: () => setBasketExportProgress(null),
    onSuccess: () => window.setTimeout(() => setBasketExportProgress(null), 800),
    onSettled: () => setIsLoading(false),
  })

  const { mutate: downloadPeriodAccountingKpiFiles } = useMutation({
    mutationFn: ({
      dateStart,
      dateEnd,
    }: {
      dateStart: string
      dateEnd: string
      isSingleDay?: boolean
    }) => downloadPeriodAccountingKpiXlsx({ dateStart, dateEnd }),
    onError: () => {
      setIsLoading(false)
      console.error('Error while downloading donnees-comptabilite file')
    },
    onSuccess: (response, variables) => {
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }))
      const link = document.createElement('a')
      link.href = url
      const fileName = variables.isSingleDay
        ? `donnees-comptabilite_${variables.dateStart}.zip`
        : `donnees-comptabilite_${variables.dateStart}_${variables.dateEnd}.zip`
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      link.parentNode?.removeChild(link)
      setIsLoading(false)
      URL.revokeObjectURL(url)
    },
  })

  const { mutate: downloadPortVieuxStatement } = useMutation({
    mutationFn: (month: string) => downloadPortVieuxVatStatementXlsx(month),
    onError: () => {
      setIsLoading(false)
      console.error('Error while downloading Port Vieux VAT statement file')
    },
    onSuccess: (response, month) => {
      const url = window.URL.createObjectURL(
        new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
      )
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `releve-ca-port-vieux-${month}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.parentNode?.removeChild(link)
      setIsLoading(false)
      URL.revokeObjectURL(url)
    },
  })

  const { mutate: downloadWorksConcilsHistoryFile } = useMutation({
    mutationFn: (date: string) => downloadWorksConcilHistoriesCsv(date),
    onError: () => {
      setIsLoading(false)
      console.error('Error while downloading works concils histories file')
    },
    onSuccess: (response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `CE-historique_${currentFormatedDate}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.parentNode?.removeChild(link)
      setIsLoading(false)
    },
  })

  const {
    internalNegoceHt,
    internalNegoceTva,
    externalNegoceHt,
    externalNegoceTva,
    sushisTtc,
    amountCb,
    amountConecs,
    unPaidAmount,
    promoAmountHt,
    promoAmountTva,
  } = accounting?.main ?? {
    amountCb: 0,
    amountConecs: 0,
    externalNegoceHt: 0,
    externalNegoceTva: 0,
    internalNegoceHt: 0,
    internalNegoceTva: 0,
    promoAmountHt: 0,
    promoAmountTva: 0,
    sushisTtc: 0,
    unPaidAmount: 0,
  }

  const handleDownload = useCallback(async () => {
    setIsLoading(true)
    downloadPeriodAccountingKpiFiles({
      dateEnd: currentFormatedDate,
      dateStart: currentFormatedDate,
      isSingleDay: true,
    })
  }, [currentFormatedDate, downloadPeriodAccountingKpiFiles])

  const handlePeriodDownload = useCallback(async () => {
    setIsLoading(true)
    downloadPeriodAccountingKpiFiles({
      dateEnd: currentFormatedEndDate,
      dateStart: currentFormatedStartDate,
      isSingleDay: false,
    })
  }, [currentFormatedStartDate, currentFormatedEndDate, downloadPeriodAccountingKpiFiles])

  const handleDownloadRevenueIntegration = useCallback(() => {
    setIsLoading(true)
    downloadRevenueIntegrationFile({
      dateEnd: currentFormatedEndDate,
      dateStart: currentFormatedStartDate,
    })
  }, [currentFormatedStartDate, currentFormatedEndDate, downloadRevenueIntegrationFile])

  const handleDownloadPortVieuxStatement = useCallback(() => {
    setIsLoading(true)
    downloadPortVieuxStatement(statementMonth.toFormat('yyyy-MM'))
  }, [statementMonth, downloadPortVieuxStatement])

  const handleDownloadPaymentsFile = useCallback(() => {
    setIsLoading(true)
    const startIsoDate = currentStartDate.toISODate()
    const endIsoDate = currentEndDate.toISODate()
    downloadPaymentsFile({ dateEnd: endIsoDate ?? '', dateStart: startIsoDate ?? '' })
  }, [currentStartDate, currentEndDate, downloadPaymentsFile])

  const handleDownloadBasketsExport = useCallback(() => {
    setIsLoading(true)
    const startIsoDate = currentStartDate.toISODate()
    const endIsoDate = currentEndDate.toISODate()
    setBasketExportProgress({
      completedDays: 0,
      currentDate: currentStartDate.toFormat('dd/MM/yyyy'),
      totalDays: Math.max(
        1,
        Math.floor(
          currentEndDate.startOf('day').diff(currentStartDate.startOf('day'), 'days').days,
        ) + 1,
      ),
    })
    downloadBasketsExportFile({ dateEnd: endIsoDate ?? '', dateStart: startIsoDate ?? '' })
  }, [currentStartDate, currentEndDate, downloadBasketsExportFile])

  return (
    <Page sx={fonts.roboto}>
      <Dialog open={basketExportProgress !== null} disableEscapeKeyDown fullWidth maxWidth="xs">
        <DialogTitle>Export des paniers</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {basketExportProgress?.completedDays === basketExportProgress?.totalDays
              ? 'Le fichier est prêt, le téléchargement va démarrer.'
              : `Traitement du ${basketExportProgress?.currentDate ?? ''}…`}
          </DialogContentText>
          <LinearProgress
            variant="determinate"
            value={
              basketExportProgress
                ? (basketExportProgress.completedDays / basketExportProgress.totalDays) * 100
                : 0
            }
            sx={{ marginTop: 2 }}
          />
          <Typography color="text.secondary" sx={{ marginTop: 1 }}>
            {basketExportProgress?.completedDays ?? 0} jour(s) sur{' '}
            {basketExportProgress?.totalDays ?? 0}
          </Typography>
        </DialogContent>
      </Dialog>
      <div style={{ display: 'flex', flexDirection: 'row', padding: 20 }}>
        <Typography style={fonts.pageTitle} flexGrow={1}>
          Comptabilité
        </Typography>
        <AuthorizedButton
          permission={EXPORT_FIGURES}
          style={{ marginRight: 40, marginTop: 8, gap: 6 }}
          variant="contained"
          disabled={isLoading}
          onClick={handleDownload}
        >
          <ArrowDownToLine strokeWidth={2} />
          Exporter le jour sélectionné
        </AuthorizedButton>
        <DatePicker
          value={currentDate}
          onChange={(date: DateTime | null) => setCurrentDate(date ?? yersterday)}
          format="dd/MM/yyyy"
        />
      </div>
      {canReadFigures ? (
        <div style={{ border: '3px solid white', padding: 10 }}>
          <div style={{ display: 'flex' }}>
            <DashboardCard
              label="Vente Négoce interne HT"
              bigValue={`${frenchFormat(internalNegoceHt)}\u00A0€ HT`}
              smallValue={`TVA : ${frenchFormat(internalNegoceTva)}\u00A0€`}
              orange
            />
            <DashboardCard
              label="Vente négoce externe HT"
              bigValue={`${frenchFormat(externalNegoceHt)}\u00A0€ HT`}
              smallValue={`TVA : ${frenchFormat(externalNegoceTva)}\u00A0€`}
            />
            <DashboardCard
              label="Vente Sushis TTC"
              bigValue={`${frenchFormat(sushisTtc)}\u00A0€`}
            />
          </div>
          <div style={{ display: 'flex' }}>
            <DashboardCard label="Ventes CB" bigValue={`${frenchFormat(amountCb)}\u00A0€`} />
            <DashboardCard
              label="Ventes Conecs"
              bigValue={`${frenchFormat(amountConecs)}\u00A0€`}
            />
            <DashboardCard
              label="Impayés clients TTC"
              bigValue={`${frenchFormat(unPaidAmount)}\u00A0€`}
            />
            <DashboardCard
              label="Montant promotions HT"
              bigValue={`${frenchFormat(promoAmountHt)}\u00A0€`}
              smallValue={`TVA : ${frenchFormat(promoAmountTva)}\u00A0€`}
            />
          </div>
        </div>
      ) : null}
      <div style={{ padding: 30 }} />
      <div
        style={{
          border: '3px solid white',
          padding: 10,
        }}
      >
        <Typography style={fonts.pageSubtitle} flexGrow={1}>
          Exporter sur une période donnée
        </Typography>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            paddingTop: 20,
          }}
        >
          <div style={{ padding: 10 }}>
            <Typography style={fonts.inputSubtitle} flexGrow={1}>
              Date de début
            </Typography>
            <DatePicker
              value={currentStartDate}
              onChange={(date: DateTime | null) => setCurrentStartDate(date ?? yersterday)}
              format="dd/MM/yyyy"
            />
          </div>
          <div style={{ padding: 10 }}>
            <Typography style={fonts.inputSubtitle} flexGrow={1}>
              Date de fin
            </Typography>
            <DatePicker
              value={currentEndDate}
              onChange={(date: DateTime | null) => setCurrentEndDate(date ?? yersterday)}
              format="dd/MM/yyyy"
            />
          </div>
          <AuthorizedButton
            permission={EXPORT_BALANCE}
            style={{ marginLeft: 40, marginTop: 40 }}
            variant="outlined"
            color="secondary"
            disabled={isLoading}
            onClick={handlePeriodDownload}
          >
            <ArrowDownToLine strokeWidth={2} />
            Exporter sur le bilan comptable
          </AuthorizedButton>
          <AuthorizedButton
            permission={{ any: [EXPORT_FIGURES, EXPORT_BALANCE] }}
            style={{ marginLeft: 40, marginTop: 40 }}
            variant="outlined"
            color="secondary"
            disabled={isLoading}
            onClick={handleDownloadRevenueIntegration}
          >
            <ArrowDownToLine strokeWidth={2} />
            Intégration chiffre d'affaires
          </AuthorizedButton>
          <AuthorizedButton
            permission={EXPORT_PAYMENTS}
            style={{ marginLeft: 40, marginTop: 40 }}
            variant="outlined"
            color="info"
            onClick={handleDownloadPaymentsFile}
            disabled={isLoading}
          >
            <ArrowDownToLine strokeWidth={2} />
            Exporter les paiements
          </AuthorizedButton>
          <AuthorizedButton
            permission={EXPORT_CE_HISTORY}
            style={{ marginLeft: 40, marginTop: 40 }}
            variant="outlined"
            color="info"
            onClick={() => downloadWorksConcilsHistoryFile(currentFormatedDate)}
            disabled={isLoading}
          >
            <ArrowDownToLine strokeWidth={2} />
            Exporter les historiques de CE
          </AuthorizedButton>
          {comptaExportPanierEnabled && (
            <AuthorizedButton
              permission={EXPORT_PAYMENTS}
              style={{ marginLeft: 40, marginTop: 40 }}
              variant="outlined"
              color="info"
              onClick={handleDownloadBasketsExport}
              disabled={isLoading}
            >
              <ArrowDownToLine strokeWidth={2} />
              Exports panier
            </AuthorizedButton>
          )}
        </div>
      </div>
      <div
        style={{
          border: '3px solid white',
          marginTop: 20,
          padding: 10,
        }}
      >
        <Typography style={fonts.pageSubtitle} flexGrow={1}>
          Relevé TVA mensuel — Port Vieux
        </Typography>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            paddingTop: 20,
          }}
        >
          <div style={{ padding: 10 }}>
            <Typography style={fonts.inputSubtitle} flexGrow={1}>
              Mois
            </Typography>
            <DatePicker
              value={statementMonth}
              onChange={(date: DateTime | null) => setStatementMonth(date ?? lastMonth)}
              views={['year', 'month']}
              openTo="month"
              format="MM/yyyy"
            />
          </div>
          <AuthorizedButton
            permission={EXPORT_FIGURES}
            style={{ marginLeft: 40, marginTop: 40 }}
            variant="outlined"
            color="secondary"
            disabled={isLoading}
            onClick={handleDownloadPortVieuxStatement}
          >
            <ArrowDownToLine strokeWidth={2} />
            Télécharger le relevé (CA par taux de TVA)
          </AuthorizedButton>
        </div>
      </div>
      <div
        style={{
          border: '3px solid white',
          marginTop: 20,
          padding: 10,
        }}
      >
        <Typography style={fonts.pageSubtitle} flexGrow={1}>
          Avertissements
        </Typography>
        {canReadWarnings && (
          <div style={{ display: 'flex', padding: 20, paddingBottom: 0 }}>
            <StoresPicker allowAllStores setCurrentStores={setWarningStoreIds} size="small" />
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', padding: 20 }}>
          {canReadWarnings ? (
            warnings?.map((warning) => (
              <Typography style={fonts.inputSubtitle} key={warning.date}>
                {warning.date} : différence de&nbsp;&nbsp;
                {frenchFormat(warning.difference)}€
              </Typography>
            ))
          ) : (
            <Typography style={fonts.inputSubtitle}>
              Vous n'êtes pas autorisé à consulter les avertissements comptables
            </Typography>
          )}
        </div>
      </div>
    </Page>
  )
}
